import { z } from 'zod';
import { FabricFacesSchema } from './fabric-faces.schema';
import {
  BanquettePolylineSchema,
  BanquetteRailSchema,
  BanquetteAdditionalPanelSchema,
  PanelRoleEnum,
} from './banquette-base-template.schema';
import { PanelFinishSchema } from './panel-finish.schema';
import {
  UpholsteryDesignKeySchema,
  UpholsteryDesignSchema,
  UpholsterySpringConfigSchema,
  UpholsteryFoamLayerSchema,
} from './upholstery-design.schema';

// ── Upholstery Design & Springs ──
// Moved to upholstery-design.schema.ts so the banquette base can reach them:
// this file imports from banquette-base-template.schema.ts, so the base cannot
// import back from here. Aliased so every existing consumer keeps compiling.

export {
  UpholsteryDesignKeySchema as BackCushionDesignTypeEnum,
  UpholsteryDesignSchema as BackCushionDesignSchema,
  UpholsterySpringConfigSchema as BackCushionSpringConfigSchema,
  UpholsteryFoamLayerSchema as BackCushionFoamLayerSchema,
};

// ── Backer Panel ──

export const BackCushionBackerPanelSchema = z.object({
  material_id: z.string().uuid().nullable().default(null),
  waste_factor: z.number().min(0).max(1).default(0.10),
});

// ── Framing (reuses rail/panel schemas from banquette base) ──

export const BackCushionFramingSchema = z.object({
  side_panel_material_id: z.string().uuid().nullable().default(null),
  rib_material_id: z.string().uuid().nullable().default(null),
  /** Profile used by any finish-enabled panel that does not name its own. */
  default_finish_id: z.string().uuid().nullable().default(null),
  side_panel_finish: PanelFinishSchema,
  rib_finish: PanelFinishSchema,
  rib_mode: z.enum(['auto', 'manual']).default('auto'),
  rib_spacing: z.number().min(0).default(24),
  rib_count: z.number().int().min(0).default(0),
  waste_factor: z.number().min(0).max(1).default(0.10),
  curved_section_count: z.number().int().min(0).default(0),
  curved_section_waste_factor: z.number().min(0).max(1).default(0),
  rails: z.array(BanquetteRailSchema).default([]),
  additional_panels: z.array(BanquetteAdditionalPanelSchema).default([]),
});

// ── Foam Layer ──
// Moved to upholstery-design.schema.ts alongside the design and spring schemas
// — see the re-export block above.

// ── Slot (reuses polyline schema from banquette base) ──

export const BackCushionSlotSchema = z.object({
  id: z.string(),
  name: z.string(),
  material_id: z.string().uuid().nullable(),
  color: z.string(),
  calc_mode: z.enum(['linear', 'area']).default('linear'),
  panel_role: PanelRoleEnum.default('front'),
  polylines: z.array(BanquettePolylineSchema).default([]),
  finish_id_side1: z.string().uuid().nullable().optional(),
  finish_id_side2: z.string().uuid().nullable().optional(),
  // Fabric-specific (only used when material has fabric_subtype)
  fabric_wrap_value: z.number().nullable().default(null),
  fabric_staple_allowance: z.number().default(1),
});

// ── Main Config ──

export const BackCushionConfigSchema = z.object({
  product_type: z.literal('banquette_back_cushion'),
  code: z.string(),
  name: z.string(),
  lf_quantity: z.number().min(0),
  manual_sections: z.boolean().default(false),
  section_count: z.number().int().min(1).default(1),
  profile_height: z.number().min(0).default(18),
  profile_depth: z.number().min(0).default(6),
  /** Reference depth the dynamic panels were authored against. Null = never
   *  baselined, which makes every delta zero. Must never auto-follow the live
   *  dimension — see the spec's freezing rule. */
  base_depth: z.number().min(0).nullable().default(null),
  /** Reference height the dynamic panels were authored against. */
  base_height: z.number().min(0).nullable().default(null),
  /** Reference width the dynamic foam layers were authored against. Null =
   *  never baselined, which makes every delta zero. Must never auto-follow the
   *  live dimension — see the spec's freezing rule. */
  base_width: z.number().min(0).nullable().default(null),

  // Design
  design: UpholsteryDesignSchema,

  // Substrate
  substrate_mode: z.enum(['simple_backer', 'full_frame', 'both']).default('full_frame'),
  backer_panel: BackCushionBackerPanelSchema.nullable().default(null),
  framing: BackCushionFramingSchema,

  // Foam layers (each layer can be a different foam type)
  foam_layers: z.array(UpholsteryFoamLayerSchema).default([]),
  foam_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra foam waste on curved sections only, blended by the curved share of
   *  the item — matches framing.curved_section_waste_factor. */
  foam_curved_waste_factor: z.number().min(0).max(1).default(0),
  dacron_material_id: z.string().uuid().nullable().default(null),
  dacron_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra dacron waste on curved sections only. */
  dacron_curved_waste_factor: z.number().min(0).max(1).default(0),

  // Springs
  springs: UpholsterySpringConfigSchema,

  // Per-line-item fabric allowances
  fabric_design_pct: z.number().min(0).nullable().optional(),
  fabric_pattern_allowance_pct: z.number().min(0).default(0),
  /** Which box faces receive fabric. Absent means all six — see resolveFaces. */
  fabric_faces: FabricFacesSchema.optional(),
  /** Whether the Oversized Rules factor applies. Absent means on — only an
   *  explicit false opts out (see resolveOversize). Deliberately not
   *  defaulted: the Cria price API reads configs as raw JSON with no schema
   *  defaults, so "absent is on" has to hold at the reader regardless. */
  oversize_enabled: z.boolean().optional(),

  // Slots
  viewport_id: z.string().uuid().nullable().optional(),
  image_scale_ppi: z.number().min(0).default(0),
  slots: z.array(BackCushionSlotSchema).default([]),
}).superRefine((cfg, ctx) => {
  const r = cfg.framing?.curved_section_count ?? 0;
  if (r > cfg.section_count) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Curved sections cannot exceed total sections',
      path: ['framing', 'curved_section_count'],
    });
  }
});

// ── Template CRUD ──

export const BackCushionTemplateSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  back_cushion_config: z.string(),
  active: z.boolean(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const CreateBackCushionTemplateSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  back_cushion_config: z.string().default('{}'),
  active: z.boolean().default(true),
});

export const UpdateBackCushionTemplateSchema = CreateBackCushionTemplateSchema.partial();

// ── Type exports ──

export type BackCushionDesignType = z.infer<typeof UpholsteryDesignKeySchema>;
export type BackCushionDesign = z.infer<typeof UpholsteryDesignSchema>;
export type BackCushionSpringConfig = z.infer<typeof UpholsterySpringConfigSchema>;
export type BackCushionBackerPanel = z.infer<typeof BackCushionBackerPanelSchema>;
export type BackCushionFraming = z.infer<typeof BackCushionFramingSchema>;
export type BackCushionFoamLayer = z.infer<typeof UpholsteryFoamLayerSchema>;
export type BackCushionSlot = z.infer<typeof BackCushionSlotSchema>;
export type BackCushionConfig = z.infer<typeof BackCushionConfigSchema>;
export type BackCushionTemplate = z.infer<typeof BackCushionTemplateSchema>;
export type CreateBackCushionTemplate = z.infer<typeof CreateBackCushionTemplateSchema>;
export type UpdateBackCushionTemplate = z.infer<typeof UpdateBackCushionTemplateSchema>;
